import { useState, useCallback, useMemo } from 'react';
export const useMenuButton = ({
  menuId,
  menuButtonId,
  hasPopup = true,
  initiallyOpen = false,
  onClick,
  externalIsOpen = false,
  setExternalIsOpen
}) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const isOpenState = setExternalIsOpen ? externalIsOpen : isOpen;
  const setIsOpenState = setExternalIsOpen || setIsOpen;
  const toggleOpenState = useCallback(newState => {
    setIsOpenState(open => {
      const nextState = newState !== undefined ? newState : !open;
      if (onClick) {
        onClick(nextState);
      }
      return nextState;
    });
  }, [onClick, setIsOpenState]);
  const onClickOutside = useCallback(() => {
    if (onClick) {
      toggleOpenState(false);
      return;
    }
    toggleOpenState(false);
  }, [onClick, toggleOpenState]);
  const onButtonClick = useCallback(() => toggleOpenState(), [toggleOpenState]);
  const closeMenu = useCallback(() => {
    setIsOpenState(false);
  }, [setIsOpenState]);
  const onButtonKeyDown = useCallback(e => {
    switch (e.key) {
      case ' ':
      case 'Enter':
      case 'Down':
        toggleOpenState();
        break;
      case 'Esc':
      case 'Escape':
        toggleOpenState(false);
        break;
      default:
        return;
    }
    e.stopPropagation();
    e.preventDefault();
  }, [toggleOpenState]);
  const menuProps = useMemo(() => Object.assign({}, hasPopup && {
    onClickOutside
  }, {
    isOpen: isOpenState
  }), [hasPopup, onClickOutside, isOpenState]);
  const buttonProps = useMemo(() => Object.assign({
    id: menuButtonId
  }, isOpenState && {
    'aria-controls': menuId
  }, isOpenState && {
    'aria-expanded': true
  }, hasPopup && {
    'aria-haspopup': true
  }, {
    onClick: onButtonClick,
    onKeyDown: onButtonKeyDown
  }), [hasPopup, isOpenState, menuButtonId, menuId, onButtonClick, onButtonKeyDown]);
  return {
    menuProps,
    buttonProps,
    closeMenu,
    setIsOpen: setIsOpenState
  };
};